// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/askPunttDeepLinkRoutes.test.tsx
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";

import {
  legacyCampaignSearchToNavigate,
  legacyChatSearchToNavigate,
  parseProjectReviewRouteSearch,
  parseReviewSearch,
  replacePathRedirect,
} from "./askPunttPaths";
import AskPunttCampaign from "./campaign";
import { parseCampaignSearch } from "./campaignSearch";
import AskPunttChat from "./chat";
import { parseChatSearch } from "./chatSearch";

import PendingComponent from "../../components/PendingComponent";
import { canAccessAskPuntt } from "../../utils/auth";
import { authLayoutRoute } from "../auth-layout/route";
import { ticketsRoute } from "../tickets/route";

function requireAskPuntt() {
  if (!canAccessAskPuntt()) {
    throw redirect({ to: ticketsRoute.to });
  }
}

function redirectLegacyCampaignSearch(
  projectId: string,
  search: Record<string, unknown>,
  options: { chatIdIsPreservedContext?: boolean } = {},
) {
  const parsed = parseCampaignSearch(search);
  const commentRaw =
    typeof search.comment === "string" ? search.comment.trim() : undefined;
  const target = legacyCampaignSearchToNavigate(
    projectId,
    { ...parsed, comment: commentRaw },
    options,
  );
  if (target) {
    throw redirect(replacePathRedirect(target) as never);
  }
}

function redirectLegacyChatSearch(
  chatId: string,
  search: Record<string, unknown>,
) {
  const parsed = parseChatSearch(search);
  const commentRaw =
    typeof search.comment === "string" ? search.comment.trim() : undefined;
  const target = legacyChatSearchToNavigate(chatId, {
    ...parsed,
    comment: commentRaw,
  });
  if (target) {
    throw redirect(replacePathRedirect(target) as never);
  }
}

const askPunttRouteOptions = {
  pendingComponent: PendingComponent,
  gcTime: 0,
  shouldReload: false,
  pendingMs: 0,
  pendingMinMs: 500,
} as const;

/** Project review workspace — path-based deep link. */
export const askPunttProjectReviewRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/projects/$projectId/reviews/$ticketId",
  validateSearch(search: Record<string, unknown>) {
    return parseProjectReviewRouteSearch(search);
  },
  beforeLoad({ params, search }) {
    requireAskPuntt();
    redirectLegacyCampaignSearch(params.projectId, search, {
      chatIdIsPreservedContext: true,
    });
  },
  component: AskPunttCampaign,
  ...askPunttRouteOptions,
});

/** Project review + comment — path-based per-comment deep link. */
export const askPunttProjectReviewCommentRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/projects/$projectId/reviews/$ticketId/comments/$commentId",
  validateSearch(search: Record<string, unknown>) {
    return parseProjectReviewRouteSearch(search);
  },
  beforeLoad({ params, search }) {
    requireAskPuntt();
    redirectLegacyCampaignSearch(params.projectId, search, {
      chatIdIsPreservedContext: true,
    });
  },
  component: AskPunttCampaign,
  ...askPunttRouteOptions,
});

/** Project personal chat — path-based deep link. */
export const askPunttProjectChatDeepLinkRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/projects/$projectId/chats/$chatId",
  beforeLoad() {
    requireAskPuntt();
  },
  component: AskPunttCampaign,
  ...askPunttRouteOptions,
});

/** Standalone chat review workspace — path-based deep link. */
export const askPunttChatReviewRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/$chatId/reviews/$ticketId",
  validateSearch(search: Record<string, unknown>) {
    // parseChatSearch gates version behind `?review=`; path-based chat review
    // routes never carry that param. Keep the pin the same way project routes do.
    return parseReviewSearch(search);
  },
  beforeLoad({ params, search }) {
    requireAskPuntt();
    redirectLegacyChatSearch(params.chatId, search);
  },
  component: AskPunttChat,
  ...askPunttRouteOptions,
});

/** Standalone chat review + comment — path-based per-comment deep link. */
export const askPunttChatReviewCommentRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/$chatId/reviews/$ticketId/comments/$commentId",
  validateSearch(search: Record<string, unknown>) {
    return parseReviewSearch(search);
  },
  beforeLoad({ params, search }) {
    requireAskPuntt();
    redirectLegacyChatSearch(params.chatId, search);
  },
  component: AskPunttChat,
  ...askPunttRouteOptions,
});

/** Canvas block focus — path-based deep link. */
export const askPunttChatCanvasRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "ask-puntt/$chatId/canvas/$messageId",
  beforeLoad({ params, search }) {
    requireAskPuntt();
    redirectLegacyChatSearch(params.chatId, search);
  },
  component: AskPunttChat,
  ...askPunttRouteOptions,
});
